import { RootState, apiSlice } from '@fly/store';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

export interface GithubConfigResponse {
  app_installation_url: string;
  github_app_installed: boolean;
  installations: Array<GithubInstallations>;
}
export interface GithubInstallations {
  github_owner: string;
  github_target_type: string;
  installation_configure_url: string;
}

export interface GithubConfig {
  appInstallationUrl?: string;
  installations: Array<GithubInstallations>;
}

/** GET /features-configuration — capability hints (auth required). */
export interface FeaturesConfigurationDistribution {
  packages: string[];
}

export interface FeaturesConfigurationResponse {
  distribution: FeaturesConfigurationDistribution;
}

interface CommonState {
  desktopConnect: boolean;
}

const initialState: CommonState = {
  desktopConnect: false,
};

// RTK Query endpoint for GitHub installation info
export const githubApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGithubInstallation: builder.query<GithubConfig, void>({
      query: () => ({
        method: 'GET',
        url: '/github/installations/info',
      }),
      transformResponse: (response: GithubConfigResponse): GithubConfig => ({
        appInstallationUrl: response.app_installation_url || '',
        installations: response.installations,
      }),
    }),
  }),
});

// RTK Query endpoint for featureToggle
export const featureToggleSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFeatureToggle: builder.query<Record<string, boolean>, void>({
      query: () => ({
        method: 'GET',
        url: '/feature-toggles',
      }),
    }),
  }),
});

export const featuresConfigurationSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFeaturesConfiguration: builder.query<
      FeaturesConfigurationResponse,
      void
    >({
      query: () => ({
        method: 'GET',
        url: '/features-configuration',
      }),
    }),
  }),
});

export const { useGetGithubInstallationQuery } = githubApiSlice;
export const { useGetFeatureToggleQuery } = featureToggleSlice;
export const { useGetFeaturesConfigurationQuery } = featuresConfigurationSlice;

export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setAppDesktopConnection: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        desktopConnect: action.payload,
      };
    },
  },
});

export const selectCommon = (state: RootState) => state.common;

export const selectDesktopConnectionStatus = createSelector(
  selectCommon,
  (common) => common.desktopConnect
);
export const { setAppDesktopConnection } = commonSlice.actions;
